import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';

import { Capacitor } from '@capacitor/core';

import AppLoggedInRoutes from './AppLoggedInRoutes';
import LayoutContainer from './components/layout/layout-container/LayoutContainer';
import Loader from './components/layout/loader/Loader';
import AgreementsGate from './components/reusable/agreements/AgreementsGate';
import GuestCookieConsentGate from './components/reusable/agreements/GuestCookieConsentGate';
import AppNavigate from './components/reusable/app-navigate/AppNavigate';
import MaintenanceErrorPage from './components/reusable/error-pages/MaintenanceErrorPage/MaintenanceErrorPage';
import NotVerifiedPage from './components/reusable/error-pages/NotVerifiedPage/NotVerifiedPage';
import ServerErrorPage from './components/reusable/error-pages/ServerErrorPage/ServerErrorPage';
import ServiceUnavailableErrorPage from './components/reusable/error-pages/ServiceUnavailableErrorPage/ServiceUnavailableErrorPage';
import UpdateRequiredPage from './components/reusable/error-pages/UpdateRequiredPage/UpdateRequiredPage';
import InstallAppDialog from './components/reusable/install-app-dialog/InstallAppDialog';
import PushPermissionGate from './components/reusable/push-permission/PushPermissionGate';
import RegistrationCompletionGate from './components/reusable/registration-completion-gate/RegistrationCompletionGate';
import VersionUpdateDialog from './components/reusable/version-update-dialog/VersionUpdateDialog';
import { LoginUserStatus, useAuth } from './context/AuthContext';
import { useHideNativeSplashOnStartupReady } from './hooks/use-hide-native-splash-on-startup-ready';
import { usePwaInstall } from './hooks/use-pwa-install';
import useCapacitorBackHandler from './hooks/useCapacitorBackHandler';
import CompleteRegistration from './pages/auth/complete-registration/CompleteRegistration';
import FbAction from './pages/auth/fb-action/FbAction';
import ResetPassword from './pages/auth/fb-action/reset-password/ResetPassword';
import Verification from './pages/auth/fb-action/verification/Verification';
import ForgotPassword from './pages/auth/forgot-password/ForgotPassword';
import SignIn from './pages/auth/signin/SignIn';
import SignUpVerification from './pages/auth/signup-verification/SignUpVerification';
import SignUp from './pages/auth/signup/SignUp';
import { useGetVersion } from './redux/api/appConfigApi';
import { RootState } from './redux/store';
import { Paths } from './shared/paths';
import { logAuth } from './utils/auth/auth-debug-logger';
import {
  VersionStatus,
  checkVersionStatus,
} from './utils/platform/version-check';
import {
  isAppInstallPromptCompleted,
  setAppInstallPromptCompleted,
} from './utils/pwa/install-prompt-storage';

function AppRoutesStartupLoader(params: {
  loginUserStatus: LoginUserStatus;
  isLoadingVersion: boolean;
}) {
  const { loginUserStatus, isLoadingVersion } = params;
  const loggedRef = React.useRef(false);

  useEffect(() => {
    if (!loggedRef.current) {
      loggedRef.current = true;
      logAuth('appRoutes:startupLoaderShown', {
        loginUserStatus,
        isLoadingVersion,
      });
    }
  }, [loginUserStatus, isLoadingVersion]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (loginUserStatus === LoginUserStatus.loading || isLoadingVersion) {
        logAuth('appRoutes:startupLoaderStillVisible', {
          loginUserStatus,
          isLoadingVersion,
          elapsedMs: 10_000,
        });
      }
    }, 10_000);

    return () => clearTimeout(timer);
  }, [loginUserStatus, isLoadingVersion]);

  return <Loader />;
}

const AppRoutes = () => {
  const maintenanceMode = useSelector(
    (state: RootState) => state.appStatus.maintenanceMode,
  );
  const serviceUnavailableMode = useSelector(
    (state: RootState) => state.appStatus.serviceUnavailableMode,
  );
  const { loginUserStatus } = useAuth();
  const {
    data: versionInfo,
    isLoading: isLoadingVersion,
    error: versionError,
  } = useGetVersion();
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const { isPwaInstalled, isMobileBrowser } = usePwaInstall();
  useCapacitorBackHandler();

  const versionCheckResult = checkVersionStatus(versionInfo);

  useHideNativeSplashOnStartupReady({
    isLoadingVersion,
    loginUserStatus,
    versionError,
  });

  useEffect(() => {
    if (
      versionCheckResult.status === VersionStatus.UpdateRecommended &&
      !isLoadingVersion
    ) {
      setShowUpdateDialog(true);
    }
  }, [versionCheckResult.status, isLoadingVersion]);

  useEffect(() => {
    if (isLoadingVersion) {
      return;
    }
    if (Capacitor.isNativePlatform()) {
      return;
    }
    if (!isMobileBrowser) {
      return;
    }
    if (isPwaInstalled) {
      return;
    }
    if (isAppInstallPromptCompleted()) {
      return;
    }
    setAppInstallPromptCompleted(true);
    setShowInstallDialog(true);
  }, [isLoadingVersion, isMobileBrowser, isPwaInstalled]);

  const handleInstallDialogClose = () => {
    setShowInstallDialog(false);
  };

  if (maintenanceMode) {
    return <MaintenanceErrorPage />;
  }

  if (serviceUnavailableMode) {
    return <ServiceUnavailableErrorPage />;
  }

  if (loginUserStatus === LoginUserStatus.loading || isLoadingVersion) {
    return (
      <div>
        <AppRoutesStartupLoader
          loginUserStatus={loginUserStatus}
          isLoadingVersion={isLoadingVersion}
        />
      </div>
    );
  }

  if (versionError) {
    return <ServerErrorPage />;
  }

  if (versionCheckResult.status === VersionStatus.UpdateRequired) {
    return (
      <Routes>
        <Route path={Paths.updateRequired} element={<UpdateRequiredPage />} />
        <Route
          path="*"
          element={<AppNavigate to={Paths.updateRequired} replace />}
        />
      </Routes>
    );
  }

  const authChecking = (element: ReactNode) => {
    if (loginUserStatus === LoginUserStatus.notVerified) {
      return <NotVerifiedPage />;
    }

    if (loginUserStatus === LoginUserStatus.error) {
      return <ServerErrorPage />;
    }

    // if (loginUserStatus === LoginUserStatus.offline) {
    // return <ServerErrorPage />; // temporary solution. there is many issues with not stable internet
    // }

    return loginUserStatus === LoginUserStatus.loggedIn ? (
      element
    ) : (
      <AppNavigate to={Paths.signIn} replace />
    );
  };

  const registrationCheckingForLoggedIn = (element: ReactNode) => {
    if (loginUserStatus !== LoginUserStatus.loggedIn) {
      return element;
    }
    return <RegistrationCompletionGate>{element}</RegistrationCompletionGate>;
  };

  return (
    <>
      {showUpdateDialog && (
        <VersionUpdateDialog
          open={showUpdateDialog}
          onClose={() => setShowUpdateDialog(false)}
        />
      )}
      {showInstallDialog && (
        <InstallAppDialog
          open={showInstallDialog}
          onClose={handleInstallDialogClose}
        />
      )}
      <GuestCookieConsentGate
        isAuthenticated={loginUserStatus === LoginUserStatus.loggedIn}>
        <Routes>
          {/*todo Create separate auth route*/}
          <Route
            path={Paths.signIn}
            element={registrationCheckingForLoggedIn(<SignIn />)}
          />
          <Route
            path={Paths.signUp}
            element={registrationCheckingForLoggedIn(<SignUp />)}
          />
          <Route
            path={Paths.signUpVerification}
            element={registrationCheckingForLoggedIn(<SignUpVerification />)}
          />
          <Route
            path={Paths.fbAction}
            element={registrationCheckingForLoggedIn(<FbAction />)}
          />
          <Route
            path={Paths.forgotPassword}
            element={registrationCheckingForLoggedIn(<ForgotPassword />)}
          />
          <Route
            path={Paths.resetPassword}
            element={registrationCheckingForLoggedIn(<ResetPassword />)}
          />
          <Route
            path={Paths.verification}
            element={registrationCheckingForLoggedIn(<Verification />)}
          />

          <Route
            path={Paths.completeRegistration}
            element={authChecking(<CompleteRegistration />)}
          />
          <Route
            path="*"
            element={authChecking(
              <RegistrationCompletionGate>
                <AgreementsGate>
                  <PushPermissionGate />
                  <LayoutContainer>
                    <AppLoggedInRoutes />
                  </LayoutContainer>
                </AgreementsGate>
              </RegistrationCompletionGate>,
            )}
          />
        </Routes>
      </GuestCookieConsentGate>
    </>
  );
};

export default AppRoutes;
